import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import WebGLFluidEnhanced from "webgl-fluid-enhanced";
import { logWarn } from "@/lib/log";

/**
 * Fullscreen WebGL fluid simulation used as the "Fantasia smoke" layer
 * for the Live Display. The simulation is driven programmatically by
 * LiveDisplay: it calls `splatAt({ x, y, color })` whenever a note
 * lands on (or leaves) the staff.
 *
 * We intentionally hide the built-in mouse/pointer handler (hover:false)
 * so audience-facing projectors don't get random splats when Dan clicks.
 * The simulation runs on a transparent canvas so it composits over the
 * existing black background.
 *
 * Notes on the underlying library (webgl-fluid-enhanced 0.8):
 *   - `container` is any DOM element; the library appends its own
 *     <canvas> to it. We pass a fullscreen absolutely-positioned div.
 *   - `splatAtLocation(x, y, dx, dy, hexColor)` splats at pixel
 *     coordinates relative to the CONTAINER, top-left origin. `dx/dy`
 *     is the velocity impulse — bigger = more explosive.
 *   - `setConfig({...})` can update runtime params without a restart.
 */
const SmokeCanvas = forwardRef(function SmokeCanvas({ style }, ref) {
  const containerRef = useRef(null);
  const simRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const sim = new WebGLFluidEnhanced(containerRef.current);
    sim.setConfig({
      // Simulation quality — mid values keep 60fps on a projector-tier GPU
      // without turning the smoke into pixelated soup.
      simResolution: 128,
      dyeResolution: 1024,
      // Fast density fade so each splat lives ~1s and is gone before the
      // next note lands (~480ms interval + ~500ms overlap). This is the
      // key knob that keeps splats from stacking up into one white pile.
      densityDissipation: 3.5,
      // Low velocity dissipation lets the initial downward impulse
      // carry the smoke well past the staff before dying, while density
      // dissipation still fades each ribbon in ~1.5s.
      velocityDissipation: 1.2,
      // Very low pressure so the fluid sim doesn't equalize dye across
      // horizontal columns — we want each splat to fall in its OWN
      // vertical ribbon rather than merge with its neighbors.
      pressure: 0.05,
      pressureIterations: 20,
      // Zero curl — vorticity was making the falling ribbons swirl and
      // merge into big blobs at the bottom instead of staying as
      // distinct columns.
      curl: 0,
      // Splats are colored by us at call time, so kill the random hue drift.
      colorful: false,
      colorUpdateSpeed: 0,
      hover: false,
      transparent: true,
      backgroundColor: "#000000",
      brightness: 0.32,
      bloom: false,
      sunrays: false,
      shading: false,
      // Small tight splats — with 12+ notes and viewport-wide even
      // distribution, each column gets ~1/12 = 8% of viewport width.
      // splatRadius 0.08 = ~8vw diameter, so columns stay distinct.
      splatRadius: 0.08,
      splatForce: 2800,
    });
    sim.start();
    simRef.current = sim;
    return () => {
      try { sim.stop(); } catch (err) { logWarn("[smoke] sim.stop failed", err); }
      simRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    /**
     * Splat at a pixel position (viewport-relative, top-left origin).
     * `dx/dy` are WebGL velocity impulses (Y-UP in WebGL space): the
     * library Y-inverts the position, not the velocity, so a splat
     * that should FALL down the DOM requires a NEGATIVE dy. Default
     * gives a gentle downward drift for callers that just want smoke.
     */
    splatAt({ x, y, color, dx = 0, dy = -900 }) {
      const sim = simRef.current;
      if (!sim) return;
      sim.splatAtLocation(x, y, dx, dy, color);
    },
    /** Random burst — useful for punctuating a whole phrase. */
    burst(count = 5) {
      simRef.current?.multipleSplats(count);
    },
  }), []);

  return (
    <div
      data-testid="smoke-canvas"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        ...style,
      }}
      aria-hidden="true"
    >
      {/* The library forcibly sets `position: relative; display: flex` on
          whatever container we pass it — that's why we wrap it here. This
          inner div is what the sim mutates; the outer fixed div keeps our
          layout intact. */}
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
});

export default SmokeCanvas;
